/**
 * Brouillon unique du configurateur.
 *
 * Post-pivot e-commerce (cahier-des-charges-ecommerce-v01.md §3.1, Lot N4) :
 * les utilisateurs ne « possèdent » plus de patrons — le multi-projets
 * (listProjects UI, cap 10, dernier-projet-ouvert en localStorage) a été
 * supprimé avec la galerie. Il ne reste qu'UN brouillon éphémère, sous une
 * clé IDB fixe, pour que le client ne perde pas sa configuration au refresh
 * avant le passage en commande.
 */

import { isIndexedDbAvailable, listProjects, loadProject, saveProject } from './indexeddb';
import type { ProjectRecord } from './indexeddb';

/** Clé IDB fixe du brouillon unique. */
export const DRAFT_ID = 'draft';

/** Nom par défaut pour un nouveau patron. La date est insérée pour différencier. */
export function defaultProjectName(now: Date = new Date()): string {
  const d = now;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `Patron du ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Charge le brouillon courant.
 *
 * Adoption douce des installations antérieures au pivot : si aucun record
 * `draft` n'existe mais que d'anciens projets multi-projets sont présents
 * dans l'IDB, le plus récemment modifié est adopté comme brouillon (recopié
 * sous la clé fixe). Les anciens records ne sont pas détruits — ils ne sont
 * simplement plus jamais lus.
 */
export async function loadDraft(): Promise<ProjectRecord | null> {
  if (!isIndexedDbAvailable()) return null;
  const draft = await loadProject(DRAFT_ID);
  if (draft) return draft;

  // Adoption : reprendre le projet le plus récent de l'ancienne ère.
  const legacy = await listProjects();
  if (legacy.length === 0) return null;
  const latest = legacy.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));
  const adopted: ProjectRecord = { ...latest, id: DRAFT_ID };
  try {
    await saveProject(adopted);
  } catch (err) {
    // Non bloquant : on peut travailler sur l'adopté même si la recopie échoue.
    console.error('[draft] Adoption du projet hérité échouée :', err);
  }
  return adopted;
}
